import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Home, Clock, Hourglass, AlertCircle, ArrowRight } from "lucide-react";
import { API } from "../../lib/auth";

export default function PaymentPending() {
  const { reference } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let timer;
    const tick = async () => {
      try { const r = await axios.get(`${API}/auth/payment/status/${reference}`); setData(r.data); } catch {}
    };
    tick(); timer = setInterval(tick, 12000);
    return () => clearInterval(timer);
  }, [reference]);

  const isActive = data?.status === "active";
  const isRejected = data?.status === "rejected";
  const isFree = data?.payment_method === "free" || data?.payment_status === "free_pending_approval";

  return (
    <div data-testid="payment-pending-page" className="min-h-screen bg-[#FFFCF2] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute -top-32 left-1/3 w-96 h-96 rounded-full bg-[#E8F5E9]/50 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 right-1/3 w-96 h-96 rounded-full bg-[#FFE4E8]/50 blur-3xl pointer-events-none" />
      <div className="relative bg-white rounded-3xl shadow-xl border border-black/8 p-9 lg:p-11 w-full max-w-md">
        <div className="flex justify-center mb-6">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center ${isActive ? "bg-[#E8F5E9]" : isRejected ? "bg-red-50" : "bg-[#E8F5E9]"}`}>
            {isActive ? <Check size={38} className="text-[#1FA868]" />
             : isRejected ? <AlertCircle size={38} className="text-red-500" />
             : <Check size={38} className="text-[#1FA868]" />}
          </div>
        </div>

        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-black text-center">
          {isActive ? "Workspace Activated!"
            : isRejected ? "Registration Rejected"
            : isFree ? "Registration Successful!"
            : "Payment Proof Submitted"}
        </h1>
        <p className="text-center text-sm text-black/60 mt-2 leading-relaxed">
          {isActive ? "Your workspace is ready. Log in to access your dashboard."
           : isRejected ? data.rejection_reason || "Please contact support for next steps."
           : isFree ? "Your free workspace is pending admin approval. We'll email you the moment it's activated."
           : "Your proof of payment has been received and is pending review by our team."}
        </p>

        <div className="mt-6 bg-[#E8F5E9]/50 border border-[#E8F5E9] rounded-xl divide-y divide-[#E8F5E9]/80">
          <div className="flex justify-between items-center px-5 py-3.5">
            <span className="text-sm text-black/60">Plan</span>
            <span className="font-semibold text-black">{data?.plan_name || "—"}</span>
          </div>
          <div className="flex justify-between items-center px-5 py-3.5">
            <span className="text-sm text-black/60">Reference</span>
            <span className="font-mono font-semibold text-black text-sm">{reference}</span>
          </div>
          <div className="flex justify-between items-center px-5 py-3.5">
            <span className="text-sm text-black/60">Status</span>
            <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
              isActive ? "text-[#1FA868]" : isRejected ? "text-red-500" : "text-amber-600"
            }`}>
              {isActive ? <Check size={14} /> : isRejected ? <AlertCircle size={14} /> : <Hourglass size={14} />}
              {isActive ? "Active" : isRejected ? "Rejected" : isFree ? "Awaiting Approval" : "Pending Verification"}
            </span>
          </div>
        </div>

        {!isActive && !isRejected && (
          <div className="mt-7">
            <h3 className="font-semibold text-black mb-4">What happens next?</h3>
            <ol className="space-y-3.5">
              {(isFree ? [
                "Our team will review your free-plan signup.",
                "Once approved, your workspace will be activated automatically.",
                "You'll receive an email with your workspace login details.",
              ] : [
                "Our team will review your payment proof.",
                "Once verified, your workspace will be created automatically.",
                "You'll receive an email with your workspace login details.",
              ]).map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-black/75">
                  <span className="w-6 h-6 rounded-full bg-[#1FA868] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  {t}
                </li>
              ))}
            </ol>
          </div>
        )}

        {isActive && data?.subdomain && (
          <Link to={`/t/${data.subdomain}/login`}
            className="mt-7 w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] text-white font-semibold inline-flex justify-center items-center gap-2 hover:opacity-95">
            Log in to workspace <ArrowRight size={16} />
          </Link>
        )}

        <Link to="/" className="mt-5 w-full py-3 rounded-xl border border-black/15 text-black font-semibold flex justify-center items-center gap-2 hover:bg-[#FFFCF2]">
          <Home size={16} /> Back to Home
        </Link>
      </div>
    </div>
  );
}
